import React, { useState } from 'react'
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material'
import { CheckRounded, PeopleOutlined, VideocamRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { protectedText } from './cameraControlRules'

// C11.5 (H-12): one key per variable line.
const PHONE_EFFECT = {
  ALL: 'the whole screen is covered, who it was is recorded and Alarms is notified',
  SENSITIVE_ONLY: 'only the protected content is covered, who it was is recorded and Alarms is notified'
}
const CAPACITY = {
  FAST: '1–2 users with the camera in use at a time with the Fast model',
  PRECISE: '1 user with the camera in use at a time with the Precise model'
}

const TITLE_ID = 'camera-control-activation-title'

// With the app's default separators i18next reads "Protected:" as namespace "Protected" and an empty key, and answers ''.
const TERM_OPTIONS = { nsSeparator: false }

// Story 213, adenda C11.5 (RN55, design Confirmar.dc.html) — turning the vision on asks to confirm that the staff has been
// informed. The checkbox only enables Activate: it is never stored nor sent (DA-3); it starts unchecked every time it
// opens, because the dialog is mounted only while open. Reopening 5 (C17.7, S220): with the phone image saved, the image
// line says so, with the SAVED days and blur.
const ActivationDialog = ({ screens, coverMode, model, scope, imageSaved = false, savedBlur = true, savedDays = 30, onActivate, onCancel }) => {
  const { t_, i18n } = useSmartTranslation()
  const [informed, setInformed] = useState(false)
  const term = (key, options = {}) => t_(key, { ...TERM_OPTIONS, ...options })

  const lines = [
    [term('Protected:'), protectedText(screens, t_, i18n.language)],
    [term('If a phone appears:'), t_(PHONE_EFFECT[coverMode])],
    imageSaved
      ? [term('A photo is saved when a phone is detected, kept for {{days}} days', { days: savedDays }), t_(savedBlur ? 'Faces are blurred' : 'Faces are not blurred')]
      : [term('No image is stored.'), t_('Only user, reason, what was being viewed, device and duration.')],
    [term('Server capacity:'), t_(CAPACITY[model])]
  ]
  // H7 (C13.7): with All of MIC everyone connected is watched, on any screen; the design (Confirmar.dc.html) makes it
  // stand out, in bold with the people icon in amber.
  if (scope === 'ALL_MIC') {
    lines.splice(2, 0, ['', t_('Everyone connected to MIC will be watched, on any screen')])
  }

  return (
    <Dialog
      open
      onClose={onCancel}
      aria-labelledby={TITLE_ID}
      PaperProps={{ sx: { maxWidth: 640, bgcolor: '#424242', borderRadius: 4, border: '1px solid #555555', backgroundImage: 'none' } }}
    >
      <DialogTitle id={TITLE_ID} component='div' sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 3, px: 3.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'primary.main', color: '#133222', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <VideocamRounded />
        </Box>
        <Typography variant='h5' component='h2' sx={{ fontWeight: 600, color: 'text.primary' }}>{t_('Activate camera control?')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3.5 }}>
        <Stack spacing={2}>
          <Typography sx={{ color: '#CFCFCF', lineHeight: 1.5 }}>
            {t_("From now on, whoever opens protected content will have to allow their device's camera to view it.")}
          </Typography>
          <Stack component='ul' spacing={1.5} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {lines.map(([term, text]) => (
              <Stack component='li' direction='row' spacing={1.5} key={term || text} data-dialog-line sx={{ color: '#E6E6E6', fontSize: 14, lineHeight: 1.5 }}>
                {!term
                  ? <PeopleOutlined sx={{ color: '#FF8F00', fontSize: 20, mt: '1px' }} />
                  : <CheckRounded sx={{ color: 'primary.main', fontSize: 20, mt: '1px' }} />}
                {term
                  ? <span><Box component='strong' sx={{ color: 'text.primary' }}>{term}</Box> {text}</span>
                  : <Box component='strong' sx={{ color: 'text.primary' }}>{text}</Box>}
              </Stack>
            ))}
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={informed} onChange={(event) => setInformed(event.target.checked)} />}
            label={t_('I have informed the staff that camera images are analysed while they view protected content')}
            sx={{ m: 0, alignItems: 'flex-start', bgcolor: '#383838', border: '1px solid #FF8F00', borderRadius: 3, p: 1.5, color: 'text.primary' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3.5, pb: 3, gap: 1.5 }}>
        <Button variant='outlined' onClick={onCancel} sx={{ borderRadius: 999, minHeight: 48 }}>{t_('Cancel')}</Button>
        <Button variant='contained' disabled={!informed} onClick={onActivate} sx={{ borderRadius: 999, minHeight: 48 }}>{t_('Activate')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ActivationDialog
